import { Box, Flex } from '@chakra-ui/react'
import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ChatHeader from '../components/ChatHeader'
import { ChatInterface } from '../components/ChatInterface/ChatInterface'
import { SidebarToggleButton } from '../components/Sidebar'
import ChatSidebar from '../components/Sidebar/ChatSidebar'

import { useQueryClient } from '@tanstack/react-query'
import { useChatLogic } from '../hooks/chat/useChatLogic'
import { CLIENT_CONFIG } from '../core/client/buildConfig'
import { useModels } from '../hooks/models/useModels'
import { useSelectedModel } from '../hooks/models/useSelectedModel'
import { useAgents } from '../hooks/agents/useAgents'
import useMobile from '../hooks/utility/useMobile'
import { useSidebar } from '../context/SidebarContext'

const ChatHomePage = () => {
  const isMobile = useMobile()
  const { isOpen: isSidebarOpen, toggle: handleSidebarToggle } = useSidebar()
  const navigate = useNavigate()
  const { agentId } = useParams()
  const queryClient = useQueryClient()
  const showModelSelection = CLIENT_CONFIG.features.model_selection === true
  const { currentAgent } = useAgents()
  const { models } = useModels()
  const { selectedModelId, selectModel } = useSelectedModel(currentAgent?.ai_model_id)



  // Callback for when a new chat is established (first message sent successfully)
  const handleNewChatEstablished = useCallback(
    (newChatId: string) => {
      // Update URL smoothly without page refresh
      const newUrl = `/a/${agentId}/c/${newChatId}`
      window.history.replaceState(null, '', newUrl)
      queryClient.invalidateQueries({ queryKey: ['chatSummaries'] })
    },
    [agentId, queryClient],
  )

  const {
    conversation,
    clearConversation,
    clearConversationAndRegenerateChatId,
    isTyping,
    setIsTyping,
    isLoading: isStreaming,
    sendMessage,
    stopStreaming,
    handleRetry,
    toggleResponseMinimized,
    chatContainerRef,
    isInputDisabled,
    hasDocumentsInConversation,
    currentPhase,
  } = useChatLogic({
    onChatEstablished: handleNewChatEstablished,
    isNewChatSession: true,
    agentId: agentId,
    modelId: showModelSelection ? selectedModelId : undefined,
  })

  const handleSelectAgent = (selectedAgentId: string) => {
    if (selectedAgentId !== agentId) {
      clearConversationAndRegenerateChatId()

      navigate(`/a/${selectedAgentId}`)
    }
  }

  const handleSelectChat = (chatId: string, chatAgentId?: string) => {

    // Clear conversation when switching to a different chat
    clearConversation()

    if (chatAgentId) {
      // Navigate to agent-specific chat route
      navigate(`/a/${chatAgentId}/c/${chatId}`)
    } else {
      // Fallback for legacy routes without agent
      navigate(`/c/${chatId}`)
    }
  }

  const handleNewChat = () => {
    clearConversationAndRegenerateChatId()

    // Navigate to agent-specific home (agentId is always available now)
    navigate(`/a/${agentId}`)
  }

  return (
    <Flex width='100vw' height='100dvh' flexDirection='row' position='relative'>
      {/* Sidebar - Always render, but it slides out when closed */}
      <ChatSidebar
        onNewChat={handleNewChat}
        isLoadingDetails={false} // No specific chat details are loaded on the home page
        selectedConversationId={undefined} // No chat is "selected" on the home page initially
        onSelectConversation={handleSelectChat}
        onSelectAgent={handleSelectAgent}
        isSidebarOpenExternal={isSidebarOpen}
        onSidebarToggle={handleSidebarToggle}
        onClearConversation={clearConversation}
      />

      <SidebarToggleButton isOpen={isSidebarOpen} onToggle={handleSidebarToggle} isFloating={true} />

      {/* Main content area */}
      <Flex
        flex='1'
        h='100dvh'
        flexDirection='column'
        minW='0'
        ml={isSidebarOpen && !isMobile ? '300px' : '0'}
        transition='margin-left 0.3s ease-in-out'
      >
        <ChatHeader isSidebarOpen={isSidebarOpen} />
        <Box flex='1' minHeight='0' overflowY='hidden'>
          <ChatInterface
            conversation={conversation}
            chatContainerRef={chatContainerRef}
            isInputDisabled={isInputDisabled}
            isTyping={isTyping}
            onSend={sendMessage}
            onRetry={handleRetry}
            onTypingChange={setIsTyping}
            hasDocumentsInConversation={hasDocumentsInConversation}
            onToggleResponseMinimized={toggleResponseMinimized}
            currentPhase={currentPhase}
            isStreaming={isStreaming}
            onStop={stopStreaming}
            models={showModelSelection ? models : undefined}
            selectedModelId={selectedModelId}
            onSelectModel={selectModel}
          />
        </Box>
      </Flex>

      {/* Mobile overlay backdrop */}
      {isMobile && isSidebarOpen && (
        <Box
          position='fixed'
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg='blackAlpha.300'
          zIndex={999}
          onClick={handleSidebarToggle}
        />
      )}

    </Flex>
  )
}

export default ChatHomePage
